<script lang="ts">
export const elSwitchSizes = ['xs', 'xxs'] as const;
type ElSwitchSizes = (typeof elSwitchSizes)[number];

export const elSwitchColors = ['primary', 'secondary'] as const;
type ElSwitchColors = (typeof elSwitchColors)[number];

export const elSwitchAligns = ['left', 'right'] as const;
type ElSwitchAligns = (typeof elSwitchAligns)[number];
</script>

<script lang="ts" setup>
import { ref, toRefs, watch } from 'vue';
import ElInputContainer from '@/forms/ElInputContainer.vue';
import { UniqueID } from '@/forms/input';

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    label: string;
    size?: ElSwitchSizes;
    color?: ElSwitchColors;
    align?: ElSwitchAligns;
    disabled?: boolean;
  }>(),
  {
    size: 'xs',
    color: 'primary',
    align: 'left',
    disabled: false,
  },
);

const emit = defineEmits<{
  (event: 'update:modelValue', val: boolean): void;
}>();

const { modelValue } = toRefs(props);
const uuid = UniqueID().getID();
const value = ref<boolean>(modelValue.value);

watch(modelValue, () => {
  value.value = modelValue.value;
  emit('update:modelValue', value.value);
});

const toggleSwitch = () => {
  if (props.disabled) return;
  value.value = !value.value;
  emit('update:modelValue', value.value);
};
</script>

<template>
  <ElInputContainer v-bind="$attrs" :no-icon="true" :name="uuid">
    <div
      class="w-full flex"
      :class="{
        'justify-start': align === 'left',
        'justify-end': align === 'right',
      }"
    >
      <input
        :id="uuid"
        :checked="modelValue"
        type="checkbox"
        class="sr-only"
        :disabled="disabled"
        :data-cy="label ? `${label.trim().replaceAll(' ', '')}-switch` : 'switch'"
      />

      <button
        class="flex items-center justify-center gap-2"
        :class="{ 'cursor-not-allowed': disabled, 'cursor-pointer': !disabled }"
        @click="toggleSwitch"
      >
        <div
          class="flex items-center shrink-0"
          :class="{
            'w-9 h-[1.375rem]': size === 'xs',
            'w-[1.625rem] h-4': size === 'xxs',
            'order-1': align === 'left',
            'order-2': align === 'right',
          }"
        >
          <div
            class="relative w-full rounded-full duration-200"
            :class="{
              'w-9 h-[1.375rem]': size === 'xs',
              'w-[1.625rem] h-4': size === 'xxs',
              'bg-neutral-300': !modelValue && !disabled,
              'bg-neutral-inactive': disabled,
              'bg-primary-active': color === 'primary' && modelValue && !disabled,
              'bg-secondary-active': color === 'secondary' && modelValue && !disabled,
            }"
          >
            <div
              class="absolute top-[0.125rem] bg-white rounded-full duration-200 drop-shadow"
              :class="{
                'w-[1.125rem] h-[1.125rem]': size === 'xs',
                'w-3 h-3': size === 'xxs',
                'left-[0.125rem]': !modelValue,
                'left-4': size === 'xs' && modelValue,
                'left-3': size === 'xxs' && modelValue,
              }"
            ></div>
          </div>
        </div>

        <label
          v-if="label"
          :for="uuid"
          :class="{
            'text-base': size === 'xs',
            'text-sm': size === 'xxs',
            'order-2': align === 'left',
            'order-1': align === 'right',
            'text-neutral-darker cursor-pointer': !disabled,
            'text-neutral-inactive cursor-not-allowed': disabled,
          }"
        >
          {{ label }}
        </label>
      </button>
    </div>
  </ElInputContainer>
</template>
